import { useState, useEffect } from 'react';
import Icon from './Icon';
import { CONTENT } from '../data/content/index';
import { ROUTE_META } from '../data/routeMeta';
import { getSectionScores, getLevel, getOverallLevel } from '../lib/scoring';
import { getRoute } from '../lib/routing';
import { COLORS, FONTS, TRACKING } from '../styles/tokens';
import { track } from '../lib/analytics';
import {
  TIER,
  DIM_SEMANTICS,
  DIM_CONFIG,
  HERO_COPY,
  generateReportDoc,
  downloadReport,
} from '../lib/report';

function useIsNarrow() {
  const [narrow, setNarrow] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 720 : false
  );
  useEffect(() => {
    const h = () => setNarrow(window.innerWidth < 720);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return narrow;
}

// First two sentences from the start of a body string
function twoSentences(text) {
  const flat = text.split('\n\n')[0].replace(/\s+/g, ' ').trim();
  let count = 0;
  let result = '';
  for (let i = 0; i < flat.length; i++) {
    result += flat[i];
    if ('.!?'.includes(flat[i]) && (i + 1 >= flat.length || flat[i + 1] === ' ')) {
      count++;
      if (count === 2) break;
    }
  }
  return result.trim();
}

function firstParagraph(text) {
  return text.split('\n\n')[0];
}


// ── Main component ──────────────────────────────────────────────────────────

export default function ResultsScreen({ answers, gateData, onRetake, emailStatus, reportDoc }) {
  const scores      = getSectionScores(answers);
  const route       = getRoute(scores);
  const overallLevel = getOverallLevel(scores.total);
  const overallPct  = Math.round((scores.total / 44) * 100);
  const content     = CONTENT[gateData.orgType] || CONTENT.smallBusiness;
  const displayRoute = gateData.orgType === 'government' ? 'discovery' : route;
  const meta        = ROUTE_META[displayRoute];
  const narrow      = useIsNarrow();

  useEffect(() => {
    track('results_viewed', { route, overallLevel, orgType: gateData.orgType });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const strongCount  = DIM_CONFIG.filter(d => getLevel(scores[d.key], d.max) === 'strong').length;
  const overallTier  = TIER[overallLevel];
  const steps        = content.actionableSteps[route] || content.actionableSteps.discovery;
  const whenReady    = content.whenReady[displayRoute];

  const pad = narrow ? '24px 20px' : '28px 32px';

  async function handleDownload() {
    // Reuse the doc built on the delivery screen when it was handed down —
    // rebuilding would spend a few hundred ms and raster both logos again.
    const doc = reportDoc || await generateReportDoc(answers, gateData);
    downloadReport(doc, gateData.firstName);
    track('report_downloaded', { route, overallLevel, orgType: gateData.orgType });
  }

  return (
    <div style={{ background: COLORS.bgApp, minHeight: '100vh', padding: '0 20px', boxSizing: 'border-box' }}>
      <main id="main-content" style={{ maxWidth: 720, margin: '0 auto', padding: '28px 0 64px' }}>

        {/* ── 1. Brand line ─────────────────────────────────────────────── */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          margin: '0 0 24px',
        }}>
          <p style={{
            fontFamily: FONTS.body,
            fontSize: 15,
            color: COLORS.textMuted,
            margin: 0,
            letterSpacing: '0.03em',
          }}>
            Aevara · AI Readiness Report
          </p>
          <button
            onClick={handleDownload}
            className="btn-outline"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: 'none',
              border: `1px solid ${COLORS.borderButton}`,
              borderRadius: 8,
              fontFamily: FONTS.body,
              fontSize: 17,
              fontWeight: 500,
              color: COLORS.textSecondary,
              cursor: 'pointer',
              padding: '13px 22px',
            }}
          >
            <Icon name="download" size={16} style={{ color: COLORS.textSecondary }} />
            Download report
          </button>
        </div>

        {/* Email delivery acknowledgement — reflects what actually happened
            on the SendingScreen so we never claim an email landed that didn't. */}
        {emailStatus && <EmailAck status={emailStatus} email={gateData.email} />}

        {/* ── 2. Hero block ─────────────────────────────────────────────── */}
        <div style={{
          background: COLORS.surface,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 12,
          padding: pad,
          marginBottom: 24,
        }}>
          {/* Top row: headline left, score right */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: narrow ? '1fr' : '1fr auto',
            gap: narrow ? 20 : 40,
            alignItems: 'center',
            marginBottom: 24,
          }}>
            {/* Left: headline + subtitle + pill */}
            <div>
              <h1 style={{
                fontFamily: FONTS.body,
                fontSize: 26,
                fontWeight: 500,
                color: COLORS.textPrimary,
                margin: '0 0 10px',
                lineHeight: 1.3,
                letterSpacing: TRACKING.tightHeadline,
              }}>
                {HERO_COPY[overallLevel].headline(gateData.firstName)}
              </h1>
              <p style={{
                fontFamily: FONTS.body,
                fontSize: 17,
                lineHeight: 1.65,
                color: COLORS.textSecondary,
                margin: '0 0 16px',
                maxWidth: 380,
              }}>
                {HERO_COPY[overallLevel].subtitle(strongCount)}
              </p>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: overallTier.fill,
                color: overallTier.text,
                fontFamily: FONTS.body,
                fontSize: 15,
                fontWeight: 500,
                padding: '4px 12px',
                borderRadius: 100,
                border: `1px solid ${overallTier.bar}40`,
              }}>
                <span style={{
                  width: 6, height: 6,
                  borderRadius: '50%',
                  background: overallTier.bar,
                  flexShrink: 0,
                }} />
                {overallTier.label} overall
              </span>
            </div>

            {/* Right: score number */}
            <div style={{ textAlign: narrow ? 'left' : 'right' }}>
              <p style={{
                fontFamily: FONTS.body,
                fontSize: 44,
                fontWeight: 500,
                color: COLORS.textPrimary,
                margin: '0 0 4px',
                lineHeight: 1,
                fontVariantNumeric: 'tabular-nums',
              }}>
                {scores.total}
                <span style={{ fontSize: 21, color: COLORS.textMuted, fontWeight: 400 }}> / 44</span>
              </p>
              <p style={{ fontFamily: FONTS.body, fontSize: 14, color: COLORS.textMuted, margin: 0 }}>
                points scored
              </p>
            </div>
          </div>

          {/* Full-width overall score bar */}
          <div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              marginBottom: 8,
            }}>
              <span style={{
                fontFamily: FONTS.body,
                fontSize: 14,
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: TRACKING.kicker,
                color: COLORS.textMuted,
              }}>
                Overall readiness
              </span>
              <span style={{
                fontFamily: FONTS.body,
                fontSize: 16,
                fontWeight: 500,
                color: overallTier.text,
                fontVariantNumeric: 'tabular-nums',
              }}>
                {overallPct}%
              </span>
            </div>
            <div
              role="progressbar"
              aria-valuenow={overallPct}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`Overall AI readiness: ${overallPct}%`}
              style={{
                height: 8,
                background: 'rgba(255,255,255,0.07)',
                borderRadius: 4,
                overflow: 'hidden',
              }}
            >
              <div style={{
                height: '100%',
                width: `${overallPct}%`,
                background: overallTier.bar,
                borderRadius: 4,
                transition: 'width 0.8s ease',
              }} />
            </div>
          </div>
        </div>

        {/* ── 3. Score breakdown ────────────────────────────────────────── */}
        <Eyebrow>Score breakdown</Eyebrow>

        <div style={{
          background: COLORS.surface,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 12,
          padding: '20px 24px',
          marginBottom: 24,
        }}>
          <GroupedBarChart scores={scores} narrow={narrow} />
        </div>

        {/* ── 4. Recommended route ─────────────────────────────────────── */}
        <Eyebrow>Recommended for you</Eyebrow>

        <div style={{
          background: COLORS.surface,
          border: `1px solid ${meta.color}33`,
          borderLeft: `3px solid ${meta.color}`,
          borderRadius: 12,
          padding: pad,
          marginBottom: 24,
          display: 'flex',
          flexDirection: narrow ? 'column' : 'row',
          alignItems: narrow ? 'flex-start' : 'center',
          justifyContent: 'space-between',
          gap: 20,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <span style={{
                fontFamily: FONTS.body,
                fontSize: 16,
                fontWeight: 500,
                color: meta.color,
                letterSpacing: '0.01em',
              }}>
                {meta.label}
              </span>
            </div>
            <p style={{
              fontFamily: FONTS.body,
              fontSize: 19,
              fontWeight: 500,
              color: COLORS.textPrimary,
              margin: '0 0 6px',
              lineHeight: 1.35,
            }}>
              {meta.headline}
            </p>
            <p style={{
              fontFamily: FONTS.body,
              fontSize: 16,
              lineHeight: 1.6,
              color: COLORS.textSecondary,
              margin: 0,
              maxWidth: 460,
            }}>
              {meta.subhead}
            </p>
          </div>
        </div>

        {/* ── 5. Category cards ─────────────────────────────────────────── */}
        <Eyebrow>Your readiness breakdown</Eyebrow>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          marginBottom: 24,
        }}>
          {DIM_CONFIG.map(dim => {
            const score = scores[dim.key];
            const level = getLevel(score, dim.max);
            const block = content.dimensions[dim.key][level];
            return (
              <CategoryCard key={dim.key} dim={dim} score={score} level={level} block={block} />
            );
          })}
        </div>

        {/* ── 6. Insight callout ────────────────────────────────────────── */}
        <div style={{
          background: 'rgba(42,192,208,0.06)',
          border: '1px solid rgba(42,192,208,0.15)',
          borderRadius: 12,
          padding: pad,
          marginBottom: 24,
        }}>
          <h2 style={{
            fontFamily: FONTS.body,
            fontSize: 15,
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: TRACKING.kicker,
            color: COLORS.holidayBlue,
            margin: '0 0 10px',
          }}>
            What you might not expect
          </h2>
          <p style={{
            fontFamily: FONTS.body,
            fontSize: 20,
            fontWeight: 500,
            color: COLORS.holidayBlue,
            margin: '0 0 12px',
            lineHeight: 1.35,
          }}>
            {content.insight.headline}
          </p>
          <p style={{
            fontFamily: FONTS.body,
            fontSize: 17,
            lineHeight: 1.65,
            color: COLORS.textSecondary,
            margin: 0,
          }}>
            {firstParagraph(content.insight.body)}
          </p>
        </div>

        {/* ── 7-8. Action steps ─────────────────────────────────────────── */}
        <Eyebrow>Three things to do this week</Eyebrow>

        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: 12,
          marginBottom: 24,
        }}>
          {steps.map((step, i) => (
            <div key={i} style={{
              background: COLORS.surface,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 12,
              padding: '16px 20px',
            }}>
              <p style={{
                fontFamily: FONTS.body,
                fontSize: 15,
                fontWeight: 500,
                color: COLORS.textMuted,
                margin: '0 0 10px',
                fontVariantNumeric: 'tabular-nums',
              }}>
                {String(i + 1).padStart(2, '0')}
              </p>
              <p style={{
                fontFamily: FONTS.body,
                fontSize: 17,
                fontWeight: 500,
                color: COLORS.textPrimary,
                margin: '0 0 8px',
                lineHeight: 1.4,
              }}>
                {step.title}
              </p>
              <p style={{
                fontFamily: FONTS.body,
                fontSize: 16,
                lineHeight: 1.6,
                color: COLORS.textSecondary,
                margin: 0,
              }}>
                {step.body}
              </p>
            </div>
          ))}
        </div>

        {/* ── 9-10. Stats ───────────────────────────────────────────────── */}
        <Eyebrow>For context</Eyebrow>

        <div style={{
          display: 'grid',
          gridTemplateColumns: narrow ? '1fr' : '1fr 1fr',
          gap: 12,
          marginBottom: 24,
        }}>
          {content.stats.headlines.slice(0, 2).map((stat, i) => (
            <div key={i} style={{
              background: COLORS.surface,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 12,
              padding: '16px 20px',
            }}>
              <p style={{
                fontFamily: FONTS.body,
                fontSize: 32,
                fontWeight: 500,
                color: COLORS.holidayBlue,
                margin: '0 0 8px',
                lineHeight: 1,
                fontVariantNumeric: 'tabular-nums',
              }}>
                {stat.figure}
              </p>
              <p style={{
                fontFamily: FONTS.body,
                fontSize: 16,
                lineHeight: 1.5,
                color: COLORS.textSecondary,
                margin: '0 0 6px',
              }}>
                {stat.label}
              </p>
              <p style={{
                fontFamily: FONTS.body,
                fontSize: 14,
                fontStyle: 'italic',
                color: COLORS.textMuted,
                margin: 0,
              }}>
                {stat.source}
              </p>
            </div>
          ))}
        </div>

        {/* ── 11. CTA block ─────────────────────────────────────────────── */}
        <div style={{
          background: COLORS.deepBlue,
          borderRadius: 12,
          padding: narrow ? '28px 20px' : '32px 36px',
          marginBottom: 32,
        }}>
          <h2 style={{
            fontFamily: FONTS.body,
            fontSize: 21,
            fontWeight: 500,
            color: COLORS.white,
            margin: '0 0 10px',
            lineHeight: 1.3,
          }}>
            {whenReady.headline}
          </h2>
          <p style={{
            fontFamily: FONTS.body,
            fontSize: 17,
            lineHeight: 1.65,
            color: 'rgba(255,255,255,0.6)',
            margin: '0 0 24px',
          }}>
            {firstParagraph(whenReady.body)}
          </p>
          <a
            href={meta.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track('cta_clicked', { route: displayRoute, ctaText: whenReady.ctaText })}
            className="btn-solid-light"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: COLORS.white,
              color: COLORS.deepBlue,
              fontFamily: FONTS.body,
              fontSize: 17,
              fontWeight: 500,
              padding: '13px 22px',
              borderRadius: 8,
              textDecoration: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            {whenReady.ctaText}
            <span className="sr-only"> (opens in a new tab)</span>
          </a>
        </div>

        {/* ── Footer ────────────────────────────────────────────────────── */}
        <footer style={{
          borderTop: `1px solid ${COLORS.border}`,
          paddingTop: 20,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 10,
        }}>
          <p style={{ fontFamily: FONTS.body, fontSize: 15, color: COLORS.textMuted, margin: 0 }}>
            © 2026 Aevara.ai
          </p>
          <button
              onClick={onRetake}
              className="btn-ghost"
              style={{
                background: 'none',
                border: 'none',
                fontFamily: FONTS.body,
                fontSize: 16,
                color: COLORS.holidayBlue,
                cursor: 'pointer',
                textDecoration: 'underline',
                textUnderlineOffset: 3,
                padding: 0,
              }}
            >
              Retake the assessment
            </button>
        </footer>

      </main>
    </div>
  );
}

// ── Category card ───────────────────────────────────────────────────────────

function CategoryCard({ dim, score, level, block }) {
  const [open, setOpen] = useState(false);
  const tier    = DIM_SEMANTICS[dim.key][level];
  const pct     = Math.round((score / dim.max) * 100);
  const panelId = `analysis-${dim.key}`;

  return (
    <div style={{
      background: COLORS.surface,
      border: `1px solid ${open ? tier.bar + '55' : COLORS.border}`,
      borderRadius: 12,
      overflow: 'hidden',
      transition: 'border-color 0.2s ease',
    }}>
      {/* Clickable header — the entire header row is the toggle */}
      <button
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-controls={panelId}
        className="btn-card"
        style={{
          width: '100%',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          padding: '16px 20px',
          display: 'block',
        }}
      >
        {/* Row 1: label + score + chevron */}
        <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <span style={{ fontFamily: FONTS.body, fontSize: 18, fontWeight: 500, color: COLORS.textPrimary }}>
            {dim.label}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
              <span
                style={{ fontFamily: FONTS.body, fontSize: 18, fontWeight: 500, color: tier.text, fontVariantNumeric: 'tabular-nums' }}
                aria-label={`${pct}%, ${score} out of ${dim.max} points`}
              >
                {pct}%
              </span>
              <span style={{ fontFamily: FONTS.body, fontSize: 14, color: COLORS.textMuted, fontVariantNumeric: 'tabular-nums' }}>
                {score} / {dim.max} pts
              </span>
            </span>
            <Icon
              name={open ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
              size={20}
              style={{ color: COLORS.textMuted }}
            />
          </span>
        </span>

        {/* Progress bar */}
        <span
          role="img"
          aria-label={`${tier.label} — ${pct}% of maximum`}
          style={{ display: 'block', height: 6, background: 'rgba(255,255,255,0.07)', borderRadius: 3, overflow: 'hidden', marginBottom: 12 }}
        >
          <span style={{ display: 'block', height: '100%', width: `${pct}%`, background: tier.bar, borderRadius: 3, transition: 'width 0.5s ease' }} />
        </span>

        {/* Tier badge + headline on same row */}
        <span style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
          <span style={{
            background: tier.fill,
            color: tier.text,
            fontFamily: FONTS.body,
            fontSize: 14,
            fontWeight: 500,
            padding: '2px 8px',
            borderRadius: 100,
            flexShrink: 0,
            marginTop: 2,
          }}>
            {tier.label}
          </span>
          <span style={{ display: 'block', fontFamily: FONTS.body, fontSize: 17, fontWeight: 500, color: COLORS.textPrimary, lineHeight: 1.4, flex: 1 }}>
            {block.headline}
          </span>
        </span>

        {/* Preview — 2-line clamp with auto ellipsis, collapsed only */}
        {!open && (
          <span style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            fontFamily: FONTS.body,
            fontSize: 16,
            lineHeight: 1.6,
            color: COLORS.textSecondary,
            marginTop: 10,
          }}>
            {block.body.split('\n\n')[0]}
          </span>
        )}
      </button>

      {/* Expandable body */}
      <div id={panelId} hidden={!open}>
        <div style={{
          padding: '0 20px 20px',
          borderTop: `1px solid ${COLORS.border}`,
          paddingTop: 16,
        }}>
          {block.body.split('\n\n').map((para, i) => (
            <p key={i} style={{
              fontFamily: FONTS.body,
              fontSize: 17,
              lineHeight: 1.65,
              color: COLORS.textSecondary,
              margin: i === 0 ? 0 : '12px 0 0',
            }}>
              {para}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Grouped bar chart ───────────────────────────────────────────────────────

function GroupedBarChart({ scores, narrow }) {
  const [hovered, setHovered] = useState(null); // dim key
  const MAX_Y    = 12;
  const CHART_H  = 130;
  const Y_AXIS_W = 28;

  const hoveredIdx = hovered ? DIM_CONFIG.findIndex(d => d.key === hovered) : -1;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'stretch' }}>
        {/* Y axis */}
        <div style={{ width: Y_AXIS_W, flexShrink: 0, position: 'relative', height: CHART_H }}>
          {[12, 8, 4, 0].map(tick => (
            <span key={tick} style={{
              position: 'absolute',
              bottom: `${(tick / MAX_Y) * 100}%`,
              right: 6,
              transform: 'translateY(50%)',
              fontFamily: FONTS.body,
              fontSize: 13,
              color: COLORS.textMuted,
              lineHeight: 1,
            }}>
              {tick}
            </span>
          ))}
        </div>

        {/* Chart area — overflow:visible so tooltip can poke above */}
        <div style={{ flex: 1, height: CHART_H, position: 'relative', display: 'flex', alignItems: 'flex-end', overflow: 'visible' }}>

          {/* Tooltip */}
          {hovered && hoveredIdx >= 0 && (() => {
            const dim   = DIM_CONFIG[hoveredIdx];
            const score = scores[dim.key];
            const tier  = DIM_SEMANTICS[dim.key][getLevel(score, dim.max)];
            const pct   = Math.round((score / dim.max) * 100);
            // Clamp left so tooltip stays inside chart on edge groups
            const leftPct = Math.min(Math.max((hoveredIdx + 0.5) * 25, 14), 86);
            return (
              <div style={{
                position: 'absolute',
                left: `${leftPct}%`,
                top: -10,
                transform: 'translate(-50%, -100%)',
                background: COLORS.surfaceHigh,
                border: `1px solid ${COLORS.border}`,
                borderLeft: `3px solid ${tier.bar}`,
                borderRadius: 8,
                padding: '10px 14px',
                pointerEvents: 'none',
                zIndex: 10,
                whiteSpace: 'nowrap',
                boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
              }}>
                <p style={{
                  fontFamily: FONTS.body,
                  fontSize: 15,
                  fontWeight: 500,
                  color: COLORS.textPrimary,
                  margin: '0 0 8px',
                }}>
                  {dim.label}
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20 }}>
                    <span style={{ fontFamily: FONTS.body, fontSize: 14, color: COLORS.textMuted }}>Your score</span>
                    <span style={{ fontFamily: FONTS.body, fontSize: 14, fontWeight: 500, color: tier.text, fontVariantNumeric: 'tabular-nums' }}>
                      {score} / {dim.max}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20 }}>
                    <span style={{ fontFamily: FONTS.body, fontSize: 14, color: COLORS.textMuted }}>Maximum</span>
                    <span style={{ fontFamily: FONTS.body, fontSize: 14, color: COLORS.textMuted, fontVariantNumeric: 'tabular-nums' }}>
                      {dim.max}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20 }}>
                    <span style={{ fontFamily: FONTS.body, fontSize: 14, color: COLORS.textMuted }}>Level</span>
                    <span style={{ fontFamily: FONTS.body, fontSize: 14, fontWeight: 500, color: tier.text }}>
                      {tier.label} · {pct}%
                    </span>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Horizontal grid lines */}
          {[4, 8, 12].map(tick => (
            <div key={tick} style={{
              position: 'absolute',
              bottom: `${(tick / MAX_Y) * 100}%`,
              left: 0,
              right: 0,
              height: 1,
              background: 'rgba(255,255,255,0.06)',
              pointerEvents: 'none',
            }} />
          ))}

          {/* One group per dimension */}
          {DIM_CONFIG.map(dim => {
            const score     = scores[dim.key];
            const tier      = DIM_SEMANTICS[dim.key][getLevel(score, dim.max)];
            const isHovered = hovered === dim.key;
            return (
              <div
                key={dim.key}
                onMouseEnter={() => setHovered(dim.key)}
                onMouseLeave={() => setHovered(null)}
                style={{
                  flex: 1,
                  height: '100%',
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'center',
                  gap: 3,
                  paddingLeft: narrow ? 4 : 8,
                  paddingRight: narrow ? 4 : 8,
                  cursor: 'default',
                  background: isHovered ? 'rgba(255,255,255,0.03)' : 'transparent',
                  borderRadius: 4,
                  transition: 'background 0.15s ease',
                }}
              >
                {/* Max bar (faded) */}
                <div style={{
                  flex: 1,
                  maxWidth: 16,
                  height: `${(dim.max / MAX_Y) * 100}%`,
                  background: isHovered ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.1)',
                  borderRadius: '2px 2px 0 0',
                  transition: 'background 0.15s ease',
                }} />
                {/* Score bar (tier-colored) */}
                <div style={{
                  flex: 1,
                  maxWidth: 16,
                  height: `${(score / MAX_Y) * 100}%`,
                  background: tier.bar,
                  borderRadius: '2px 2px 0 0',
                  opacity: isHovered ? 1 : 0.8,
                  transition: 'opacity 0.15s ease',
                }} />
              </div>
            );
          })}
        </div>
      </div>

      {/* X-axis labels — dim slightly when another is hovered */}
      <div style={{ display: 'flex', paddingLeft: Y_AXIS_W, marginTop: 8 }}>
        {DIM_CONFIG.map(dim => {
          const score     = scores[dim.key];
          const tier      = DIM_SEMANTICS[dim.key][getLevel(score, dim.max)];
          const isHovered = hovered === dim.key;
          return (
            <div key={dim.key} style={{
              flex: 1,
              paddingLeft: narrow ? 4 : 8,
              paddingRight: narrow ? 4 : 8,
              textAlign: 'center',
              opacity: hovered && !isHovered ? 0.45 : 1,
              transition: 'opacity 0.15s ease',
            }}>
              <div style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.textMuted, lineHeight: 1.35, marginBottom: 3 }}>
                {narrow ? dim.shortLabel : dim.label}
              </div>
              <div style={{ fontFamily: FONTS.body, fontSize: 16, fontWeight: 500, color: tier.text, fontVariantNumeric: 'tabular-nums' }}>
                {Math.round((score / dim.max) * 100)}%
              </div>
              <div style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.textMuted, fontVariantNumeric: 'tabular-nums' }}>
                {score}/{dim.max} pts
              </div>
            </div>
          );
        })}
      </div>

      {/* Custom legend */}
      <div style={{
        display: 'flex',
        gap: 20,
        paddingLeft: Y_AXIS_W,
        marginTop: 14,
        paddingTop: 12,
        borderTop: `1px solid ${COLORS.border}`,
      }}>
        <LegendItem color="rgba(255,255,255,0.1)" label="Maximum possible" />
        <LegendItem color={COLORS.accent}          label="Your score" />
      </div>
    </div>
  );
}

function LegendItem({ color, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ width: 14, height: 6, background: color, borderRadius: 2, flexShrink: 0 }} />
      <span style={{ fontFamily: FONTS.body, fontSize: 14, color: COLORS.textMuted }}>{label}</span>
    </div>
  );
}

// ── Shared ──────────────────────────────────────────────────────────────────

// Renders as <h2> so screen readers can navigate the page by headings.
// Visual styles match the design's kicker/eyebrow treatment.
function Eyebrow({ children }) {
  return (
    <h2 style={{
      fontFamily: FONTS.body,
      fontSize: 15,
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: TRACKING.kicker,
      color: COLORS.textMuted,
      margin: '0 0 12px',
    }}>
      {children}
    </h2>
  );
}

// ── Email acknowledgement ───────────────────────────────────────────────────
// Mirrors the outcome resolved on the SendingScreen so the user is never told
// "check your inbox" for an email that didn't go out. 'sending' covers the
// escape-hatch case where delivery was still in flight when they clicked
// through — the send keeps running in the background.

function EmailAck({ status, email }) {
  const palette = {
    sent:    { icon: 'mark_email_read', color: COLORS.holidayBlue, border: 'rgba(42,192,208,0.30)', fill: 'rgba(42,192,208,0.08)' },
    sending: { icon: 'schedule',        color: COLORS.accent,      border: 'rgba(37,99,255,0.24)',  fill: 'rgba(37,99,255,0.06)' },
    unsent:  { icon: 'info',             color: COLORS.textMuted,   border: COLORS.border,          fill: 'transparent' },
  }[status];
  if (!palette) return null;

  const copy = {
    sent:    `We've emailed your full PDF report to ${email}. If it isn't there in a minute, check your spam folder.`,
    sending: `Still sending your report to ${email} — it should land shortly.`,
    unsent:  `We couldn't email your report this time. You can read it below or download the PDF above.`,
  }[status];

  return (
    <div
      role="status"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        background: palette.fill,
        border: `1px solid ${palette.border}`,
        borderRadius: 10,
        padding: '12px 16px',
        margin: '0 0 24px',
      }}
    >
      <Icon name={palette.icon} size={20} style={{ color: palette.color, flexShrink: 0, marginTop: 1 }} />
      <p style={{
        fontFamily: FONTS.body,
        fontSize: 15,
        lineHeight: 1.55,
        color: COLORS.textSecondary,
        margin: 0,
      }}>
        {copy}
      </p>
    </div>
  );
}
